/**
 * Paths an anonymous visitor is allowed to stay on.
 *
 * A stale token in localStorage must never bounce someone off these pages:
 * doing so hijacks the activation and password-recovery flows, which are
 * exactly the flows a user reaches while signed out.
 *
 * Matching is prefix-aware on purpose. The recovery routes carry their token
 * in the path (/verify-email/<token>, /set-password/<token>,
 * /reset-password/<token>), so an exact comparison would match only the
 * token-less variants and let the redirect fire on the URLs that matter most.
 */
export const PUBLIC_PATHS = [
  '/',
  '/login',
  '/forgot-password',
  '/reset-password',
  '/set-password',
  '/verify-email',
  '/conformite-cndp',
  // Anonymous-by-design pages. /app is the mobile download page the printed QR
  // codes point at, and /legal/* are the privacy and terms URLs declared to the
  // App Store and Google Play: a visitor reaching either has no reason to hold
  // a token, and a stale one must not bounce them to /login.
  '/app',
  '/legal',
] as const;

/**
 * True when `pathname` is a public page, including its token-bearing children.
 * "/verify-email/abc123" is public; "/verify-emailing" is not.
 */
export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some(
    (path) => pathname === path || (path !== '/' && pathname.startsWith(`${path}/`)),
  );
}
